import { Icon } from '@aqarmap/aqarmap-ui';
import { useTranslation } from 'react-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Languages } from '~/constants/enums';
import { useGetRelatedListingsQuery } from '~/redux/api/appApiSlice';
import RelatedListingsLoader from '~/pages/LeadFollowUp/RelatedListingsLoader';
import StackedListingCard from '~/pages/LeadFollowUp/StackedListingCard';
import { relatedListingsSliderBreakPoints } from '~/pages/LeadFollowUp/utils';
import useSwiperNavigation from '~valuation/hooks/useSwiperNavigation';
import SliderNavigation from '~valuation/pages/ValuationResults/RelatedListings/SliderNavigation';

export default function RelatedListings({
  listingId,
  propertyType,
  location,
  section,
}: {
  listingId: string;
  propertyType: { slug: string };
  location: { slug: string };
  section: { slug: string };
}) {
  const { t, i18n } = useTranslation('app');
  const locale = i18n.language;
  const isRtl = locale === Languages.ARABIC;
  const { sliderRef, handleInit, handleSlideChange, ...navigationProps } =
    useSwiperNavigation();
  const { data: listings, isLoading } = useGetRelatedListingsQuery({
    locale,
    listingId,
  });

  if (isLoading) return <RelatedListingsLoader />;
  if (!listings?.length) return null;

  const discoverMoreUrl = `${process.env.REACT_APP_BASE_URL}/${locale}/${section.slug}/${propertyType.slug}/${location.slug}`;

  return (
    <section className="flex flex-col gap-2x md:gap-3x">
      <div className="flex items-center justify-between">
        <p className="text-title_4 text-black">
          {t('leadFollowUpRelatedListingsTitle')}
        </p>
        <a
          href={discoverMoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-0.5x text-link text-blue"
        >
          {t('showMore')}
          <Icon name={isRtl ? 'chevron-left' : 'chevron-right'} size="16px" />
        </a>
      </div>
      <div className="relative">
        <SliderNavigation {...navigationProps} />
        <Swiper
          ref={sliderRef}
          onInit={handleInit}
          onSlideChange={handleSlideChange}
          breakpoints={relatedListingsSliderBreakPoints}
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          touchStartPreventDefault={false}
          passiveListeners={false}
          className="!h-[340px]"
        >
          {listings.map((listing) => (
            <SwiperSlide key={`related-listing-${listing.id}`}>
              <StackedListingCard listing={listing} section={section} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
}
